import { useModal } from '@billy/design-system'
import { createContext, ReactElement, ReactNode, useCallback, useContext, useMemo, useRef, useState } from 'react'

import { ModalId } from '../../../../../../../../../../../../../enums/modalId'
import { useRecurringInvoiceForm } from '../../../../../../../../../contexts/recurringInvoiceFormContext'

interface RegisteredMessage {
  body: string
  to: string[]
}

interface ContextState {
  closeRecurringInvoiceEmailTemplateModal: () => void
  saveRecurringInvoiceEmailTemplate: () => void
}

const RecurringInvoiceEmailTemplateContext = createContext<ContextState | undefined>(undefined)

interface RecurringInvoiceEmailTemplateContextProviderProps {
  children: ReactNode
}

export const RecurringInvoiceEmailTemplateContextProvider = ({
  children,
}: RecurringInvoiceEmailTemplateContextProviderProps): ReactElement => {
  const hasEmailTemplateBeenSaved = useRef(false)
  const { recurringInvoiceForm } = useRecurringInvoiceForm()
  const { getValues, setValue, trigger } = recurringInvoiceForm

  const [registeredMessage, setRegisteredMessage] = useState<RegisteredMessage>({
    body: getValues('message.body') ?? '',
    to: getValues('message.to') ?? [],
  })

  const { close: closeRecurringInvoiceEmailTemplateModal } = useModal(ModalId.RecurringInvoiceEditEmailTemplateModal, {
    onClose: () => {
      // When the modal is closed any other way than the "Save" button (X, Escape, backdrop,
      // Cancel), revert the shared form fields back to the last saved ("registered") values.
      if (!hasEmailTemplateBeenSaved.current) {
        setValue('message.to', registeredMessage.to)
        setValue('message.body', registeredMessage.body)
      }
      hasEmailTemplateBeenSaved.current = false
    },
  })

  const saveRecurringInvoiceEmailTemplate = useCallback(async () => {
    const isEmailTemplateValid = await trigger(['message', 'message.to', 'message.body'])

    if (isEmailTemplateValid) {
      hasEmailTemplateBeenSaved.current = true
      setRegisteredMessage({ body: getValues('message.body') ?? '', to: getValues('message.to') ?? [] })
      closeRecurringInvoiceEmailTemplateModal()
    }
  }, [closeRecurringInvoiceEmailTemplateModal, getValues, trigger])

  const contextValue = useMemo(
    () => ({ closeRecurringInvoiceEmailTemplateModal, saveRecurringInvoiceEmailTemplate }),
    [closeRecurringInvoiceEmailTemplateModal, saveRecurringInvoiceEmailTemplate],
  )

  return (
    <RecurringInvoiceEmailTemplateContext.Provider value={contextValue}>
      {children}
    </RecurringInvoiceEmailTemplateContext.Provider>
  )
}

export const useRecurringInvoiceEmailTemplate = () => {
  const context = useContext(RecurringInvoiceEmailTemplateContext)

  if (!context) {
    throw new Error('RecurringInvoiceEmailTemplateContextProvider is missing in the module!')
  }

  return context
}
